import { Badge, Dialog, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { KHONG_CO, soVN } from '../../../lib/dinh-dang';
import { KET_LUAN, PHAN_QUYET } from '../constants';
import { useSoSanhPhien } from '../hooks/useThamDinh';
import type { PhanQuyet } from '../types';

export interface HopSoSanhProps {
  open: boolean;
  onClose: () => void;
  phienId: string;
  /** Hồ sơ có lần nộp trước; không có thì nút mở hộp này đã bị ẩn. */
  coLanTruoc: boolean;
}

/** Tông của một dòng diff. Màu từ token, không phải class màu thô. */
const TONG_DIFF = {
  them: 'bg-success-50 text-success-800',
  bo: 'bg-danger-50 text-danger-800',
  giu: 'text-content-secondary',
} as const;

/** Phán quyết hoặc dấu gạch khi tiêu chí không có ở lần nộp ấy. */
function NhanPhanQuyet({ pq }: { pq: PhanQuyet | null }) {
  const { t } = useT();
  if (!pq) return <span className="text-content-muted">{KHONG_CO}</span>;
  return <Badge variant={PHAN_QUYET[pq].variant}>{t(PHAN_QUYET[pq].khoa)}</Badge>;
}

/**
 * So sánh hai lần nộp của một hồ sơ (đặc tả mục 11.5).
 *
 * Thứ tự cố ý: **điểm và kết luận trước, tiêu chí đổi phán quyết sau, diff văn bản cuối**. Người thẩm định
 * mở màn này để trả lời "sửa hồ sơ xong thì đổi được gì" — câu trả lời là điểm, không phải một trang diff.
 */
export function HopSoSanh({ open, onClose, phienId, coLanTruoc }: HopSoSanhProps) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const q = useSoSanhPhien(open ? phienId : undefined, coLanTruoc);
  const d = q.data;

  return (
    <Dialog open={open} onClose={onClose} title={t('thamDinh.raSoat.soSanh')}>
      <div className="space-y-4 pt-2">
        {q.isPending && (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {q.error && (
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
            {dichLoi(q.error)}
          </p>
        )}

        {d && (
          <>
            <p className="text-sm text-content-muted">
              {t('thamDinh.raSoat.soSanhLan', {
                sau: soVN(d.lan_nop, locale), truoc: soVN(d.phien_truoc.lan_nop, locale),
              })}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {([['truoc', d.diem_truoc, d.ket_luan_truoc], ['sau', d.diem_sau, d.ket_luan_sau]] as const)
                .map(([ben, diem, ketLuan]) => (
                  <div key={ben} className="rounded-lg border border-default p-3">
                    <p className="text-2xs text-content-muted">
                      {t('thamDinh.raSoat.lanNop', {
                        so: soVN(ben === 'truoc' ? d.phien_truoc.lan_nop : d.lan_nop, locale),
                      })}
                    </p>
                    <p className="mt-1 text-lg font-medium">
                      {diem === null ? KHONG_CO : `${soVN(diem, locale)}/100`}
                    </p>
                    {ketLuan && <Badge variant={KET_LUAN[ketLuan].variant}>{t(KET_LUAN[ketLuan].khoa)}</Badge>}
                  </div>
                ))}
            </div>

            <section aria-label={t('thamDinh.raSoat.soSanhThayDoi', { so: soVN(d.thay_doi.length, locale) })}>
              <h3 className="text-sm font-medium">
                {t('thamDinh.raSoat.soSanhThayDoi', { so: soVN(d.thay_doi.length, locale) })}
              </h3>
              {d.thay_doi.length === 0 ? (
                <p className="mt-1 text-sm text-content-muted">{t('thamDinh.raSoat.soSanhKhongDoi')}</p>
              ) : (
                <ul className="mt-2 divide-y divide-default rounded border border-default">
                  {d.thay_doi.map((x) => (
                    <li key={x.ma} className="flex flex-wrap items-center gap-2 px-3 py-2">
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {x.ten}
                        {x.loai_tru && (
                          <span className="ms-1 text-2xs text-danger-700">{t('thamDinh.soan.loaiTru')}</span>
                        )}
                      </span>
                      <NhanPhanQuyet pq={x.truoc} />
                      <span aria-hidden="true" className="text-content-muted">→</span>
                      <NhanPhanQuyet pq={x.sau} />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-label={t('thamDinh.raSoat.soSanhVanBan')}>
              <h3 className="text-sm font-medium">{t('thamDinh.raSoat.soSanhVanBan')}</h3>
              {d.van_ban_bi_cat && (
                <p className="mt-1 text-sm text-warning-800">{t('thamDinh.raSoat.soSanhBiCat')}</p>
              )}
              <ul className="mt-2 max-h-80 overflow-y-auto rounded border border-default font-mono text-2xs">
                {d.van_ban.map((x, i) => (
                  <li
                    key={i}
                    className={cn('whitespace-pre-wrap px-2 py-0.5',
                      x.loai === 'gap' ? 'bg-subtle text-content-muted' : TONG_DIFF[x.loai])}
                  >
                    {x.loai === 'gap'
                      ? `⋯ ${t('thamDinh.raSoat.soSanhGap', { so: soVN(x.so, locale) })}`
                      : `${x.loai === 'them' ? '+' : x.loai === 'bo' ? '−' : ' '} ${x.chu}`}
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </Dialog>
  );
}
